import { useEffect, useState } from "react";
import { Image as ImageIcon, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Spinner } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useStorageObjectUrl } from "@/hooks/useStorageObjectUrl";

export interface ItemThumbnailProps {
  /** Canonical storage path or proxy URL of the item's photo. */
  imageUrl?: string | null;
  alt?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  imgClassName?: string;
  /** Icon shown when the image is missing or fails to load. Defaults to ImageIcon. */
  fallbackIcon?: LucideIcon;
  fallbackIconClassName?: string;
  /** Omit fixed sm/md/lg dimensions and fill the parent box. */
  fill?: boolean;
  objectFit?: "cover" | "contain";
  rounded?: "lg" | "xl" | "none";
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-12 w-12",
  lg: "h-16 w-16",
};

const roundedClasses = {
  lg: "rounded-lg",
  xl: "rounded-xl",
  none: "rounded-none",
};

export function ItemThumbnail({
  imageUrl,
  alt,
  size = "md",
  className,
  imgClassName,
  fallbackIcon: FallbackIcon = ImageIcon,
  fallbackIconClassName,
  fill = false,
  objectFit = "cover",
  rounded = "lg",
}: ItemThumbnailProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("components");
  const resolvedAlt = alt ?? t("itemThumbnailAlt");
  const storage = useStorageObjectUrl(imageUrl ?? undefined);
  const resolvedImageUrl =
    storage.status === "ready" ? storage.url : undefined;
  const isResolving = storage.status === "loading";
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [resolvedImageUrl]);

  const showImage = Boolean(resolvedImageUrl) && !imageError;
  const roundedClass = roundedClasses[rounded];

  return (
    <div
      className={cn(
        "shrink-0 overflow-hidden bg-surface-100",
        roundedClass,
        !fill && sizeClasses[size],
        className,
      )}
    >
      {isResolving ? (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner
            size="sm"
            variant="muted"
            loadingLabel={tCommon("loading")}
          />
        </div>
      ) : showImage ? (
        <img
          src={resolvedImageUrl}
          alt={resolvedAlt}
          className={cn(
            "h-full w-full",
            objectFit === "contain" ? "object-contain" : "object-cover",
            imgClassName,
          )}
          loading="lazy"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <FallbackIcon
            className={cn("h-5 w-5 text-surface-300", fallbackIconClassName)}
            aria-hidden
          />
        </div>
      )}
    </div>
  );
}
